/**
 * «Решить» — the home screen, Spec 022 (round-2 mockups).
 *
 * Only what waits for me, and each card is decided on the spot:
 * - a code review (first: it holds the merge): «Проверить» shows the changes and
 *   «Согласовать эту версию» right in the card — no hunt through the task;
 * - a result: «Проверить» opens the full result and how to check it, and only
 *   there «Подтвердить» (Kostya's agent: confirm only what you have checked);
 * - an idea: by role — the partner «В задачу / Обсудить / Отклонить», the
 *   author «Обсудить / Отозвать» (an author never converts their own idea);
 * - a request addressed to me (Spec 033): one of its answers, or my words and «Отправить»
 *   right in the card; an answer to my own request: «Принять» or «Уточнить»;
 * - what a run of an agent asks (Spec 027, D9): a question with its answers, or a right —
 *   «Разрешить в этом запуске» / «Отказать»; only the person the run works for decides;
 * - a staff member's new version (Spec 043): «Что изменилось» opens the approval sheet of its
 *   card, with «Одобрить» there; its helpers alone — «Одобрить помощников» right in the card;
 * - a unit's kit version (Spec 055): the difference in words and «Одобрить версию N» right in
 *   the card — one «да» of any of the top unit's people puts it in force;
 * - merged code of my task the server does not run yet (Spec 044), next to the code reviews:
 *   «Выпустить» and how the release goes right in the card;
 * - the cases on review, one card for all of them (Spec 025);
 * - «Опыт не разобран», one line for all the notes of tasks closed without it (Spec 053);
 * - my tasks past their deadline (task d9165be7): «Перенести срок» right in the card;
 * - my tasks with an obstacle.
 * Under them, one quiet block: what the agents are doing, what waits for the
 * partner. On a computer the list sits left and the task opens on the right.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Code, type CodeReview } from "@/api/code";
import { Experience, notesLine } from "@/api/experience";
import { OrderCode, type OrderCodeReview } from "@/api/orders";
import { PersonRequests } from "@/api/requests";
import { AgentRequests, Agents, Ideas, Overview, Tasks } from "@/api/resources";
import type { ActiveTask, AgentRequestDecision, AgentRequestKind, OverviewOut, PersonRequestMove, WaitingItem } from "@/api/types";
import { UnitKits } from "@/api/units";
import { CardChangeBody } from "@/components/AgentCardChange";
import { ApproveSheet } from "@/components/AgentForm";
import { phases } from "@/components/CodeReleases";
import { OrderCodeReviewPanel } from "@/components/OrderCodeReview";
import { CodeReviewPanel } from "@/components/TaskCode";
import { TaskResultReview } from "@/components/TaskResultReview";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { DateField, TextArea } from "@/components/ui/Field";
import { SkeletonList } from "@/components/ui/Skeleton";
import { Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { ago, Caption, EmptyNote, genitive, ListPane, ProfileButton, ScreenHeader, useIsDesktop, whoDid } from "@/kit";
import { approvalNeed, decidedText, decisionLabels, permissionNote, REQUEST_KIND, ruleLabel, ruleOffer } from "@/lib/agents";
import { cn } from "@/lib/cn";
import { casesInReview, deadlineOf, forMe, ideasWord, localDay, myBlocked, myOverdue, myReleases, waitingCount, waitingOnOthers } from "@/lib/decide";
import { waitsLine } from "@/lib/feed";
import { TaskScreen } from "@/screens/TaskScreen";
import { linkFor } from "@/views/OverviewView";

export function DecideScreen() {
  const desktop = useIsDesktop();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const overview = useQuery({ queryKey: ["overview"], queryFn: Overview.get, refetchOnMount: "always", refetchInterval: 30_000 });
  const codeReviews = useQuery({ queryKey: ["code-reviews-for-me"], queryFn: Code.forMe, refetchInterval: 30_000 });
  // Spec 052, step 6: the reviews of my orders' code, beside the tasks' ones.
  const orderReviews = useQuery({ queryKey: ["order-code-reviews-for-me"], queryFn: OrderCode.forMe, refetchInterval: 60_000 });
  const orderReviewCount = orderReviews.data?.reviews.length ?? 0;
  // Spec 053, step 2: tasks closed without looking their experience through — one line, not a
  // card each; people decide them in «Знания».
  const unsorted = useQuery({ queryKey: ["experience-notes"], queryFn: Experience.list, refetchInterval: 300_000 });
  const unsortedCount = unsorted.data?.length ?? 0;

  // A person looked: «changes since last look» starts from the snapshot shown.
  const marked = useRef(false);
  const shown = overview.isFetchedAfterMount ? overview.data?.generated_at : undefined;
  useEffect(() => {
    if (shown && !marked.current) {
      marked.current = true;
      Overview.seen(shown).catch(() => undefined);
    }
  }, [shown]);

  const open = params.get("open");
  const openTask = (id: string, block?: string) => {
    if (desktop) {
      const next = new URLSearchParams(params);
      next.set("open", id);
      if (block) next.set("block", block);
      else next.delete("block");
      setParams(next, { replace: true });
    } else navigate(`/tasks/${id}${block ? `?block=${block}` : ""}`);
  };
  const closeTask = () => {
    const next = new URLSearchParams(params);
    next.delete("open");
    next.delete("block");
    setParams(next, { replace: true });
  };
  // Spec 043: «Посмотреть разницу» in Telegram opens «Решить» right on a staff member's approval.
  const approving = params.get("approve");
  const stopApproving = () => {
    const next = new URLSearchParams(params);
    next.delete("approve");
    setParams(next, { replace: true });
  };

  const data = overview.data;
  const mine = data?.me.id;
  const blocked = myBlocked(data);
  const overdue = myOverdue(data);
  const count = waitingCount(data) + (codeReviews.data?.reviews.length ?? 0) + orderReviewCount;
  const today = new Date().toLocaleDateString("ru-RU", { weekday: "long", day: "numeric", month: "long" });

  const list = (
    <div className="flex flex-col gap-3">
      <ScreenHeader title="Решить" sub={data ? `${count ? `${count} ${count === 1 ? "ждёт" : "ждут"} тебя` : "Ничего не ждёт"} · ${today}` : today} trailing={<ProfileButton />} />
      {overview.isLoading && <SkeletonList count={3} />}
      {overview.isError && !data && (
        <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void overview.refetch()}>Повторить</Button>}>
          Не удалось загрузить. Проверь связь.
        </EmptyNote>
      )}
      {data && count === 0 && !codeReviews.isLoading && !codeReviews.isError && !(data.sent_back ?? []).length && (
        <EmptyNote>Всё решено. Новые результаты, идеи и вопросы агентов появятся здесь.</EmptyNote>
      )}
      {codeReviews.data?.reviews.map((r) => <CodeReviewCard key={r.id} review={r} me={mine ?? undefined} selected={open === r.task_id} onOpen={openTask} />)}
      {codeReviews.data?.truncated && <p className="text-meta text-p-text-muted">Показана часть запросов на проверку кода.</p>}
      {orderReviews.data?.reviews.map((r) => <OrderCodeReviewCard key={r.id} review={r} me={mine ?? undefined} />)}
      {myReleases(data).map((w) => <ReleaseCard key={`release-${w.request_id ?? w.item.id}`} item={w} selected={open === w.item.id} onOpen={openTask} />)}
      {forMe(data).map((w) => (
        <DecisionCard key={`${w.kind}-${w.request_id ?? w.item.id}`} item={w} me={mine ?? undefined} selected={open === w.item.id} onOpen={openTask} />
      ))}
      {codeReviews.isError && <EmptyNote action={<Button variant="secondary" onClick={() => void codeReviews.refetch()}>Повторить</Button>}>Запросы на проверку кода не загрузились.</EmptyNote>}
      {casesInReview(data) > 0 && <CasesCard count={casesInReview(data)} />}
      {unsortedCount > 0 && (
        <Link to="/knowledge/experience" className="inline-flex min-h-11 items-center self-start text-meta font-semibold text-p-link">
          Опыт не разобран · {notesLine(unsortedCount)}
        </Link>
      )}
      {overdue.map((w) => <OverdueCard key={`overdue-${w.item.id}`} item={w} selected={open === w.item.id} onOpen={openTask} />)}
      {blocked.map((a) => <BlockedCard key={a.item.id} task={a} selected={open === a.item.id} onOpen={openTask} />)}
      <SentBack items={data?.sent_back ?? []} open={open} onOpen={openTask} />
      {data && <Happening data={data} onOpen={openTask} />}
      {/* PC audit 28.09, point 8: the change log had no way in after the old home screen went. */}
      {data && (
        <Link to="/changes" className="inline-flex min-h-11 items-center self-start text-meta font-semibold text-p-link">
          Что изменилось{data.changes_total ? ` · ${data.changes_total}` : ""}
        </Link>
      )}
      {approving && <ApproveFromLink agentId={approving} onClose={stopApproving} />}
    </div>
  );

  if (!desktop) return list;
  const cards = myReleases(data).length + forMe(data).length + overdue.length + blocked.length + (data?.sent_back?.length ?? 0) + (codeReviews.data?.reviews.length ?? 0) + orderReviewCount;
  return (
    <ListPane
      list={list}
      open={Boolean(open)}
      onClose={closeTask}
      pane={open ? <TaskScreen key={open} taskId={open} embedded /> : null}
      placeholder={cards ? <EmptyNote>Выбери карточку слева — задача откроется здесь.</EmptyNote> : null}
    />
  );
}

function Who({ item }: { item: WaitingItem }) {
  if (!item.by) return null;
  return (
    <p className="flex items-center gap-2 text-meta text-p-text-muted">
      <span aria-hidden="true" className="flex h-[22px] w-[22px] items-center justify-center rounded-full border border-p-border bg-p-surface-2 text-[10px] font-bold text-p-text">
        {item.by.name.slice(0, 1)}
      </span>
      {whoDid(item.by.name, item.via_agent)} · {ago(item.since)}
    </p>
  );
}

/**
 * Results I sent back with «поправить» (PC audit 28.09, point 2): the agent is to
 * send a new one, yet I may confirm this one as it is. Kostya could not find them
 * — «Решить» said «Ничего не ждёт». Listed apart and quieter: not «ждёт тебя».
 */
function SentBack({ items, open, onOpen }: { items: WaitingItem[]; open: string | null; onOpen: (id: string) => void }) {
  if (!items.length) return null;
  return (
    <section className="mt-2 space-y-2">
      <Caption>Ты просил поправить</Caption>
      {items.map((w) => <ResultCard key={`back-${w.item.id}`} item={w} selected={open === w.item.id} onOpen={onOpen} sentBack />)}
    </section>
  );
}

function DecisionCard({ item, me, selected, onOpen }: {
  item: WaitingItem; me?: string; selected: boolean; onOpen: (id: string) => void;
}) {
  if (item.kind === "result_review") return <ResultCard item={item} selected={selected} onOpen={onOpen} />;
  if (item.kind === "idea_review") return <IdeaCard item={item} me={me} />;
  if (item.kind === "agent_request") return <AgentRequestCard item={item} />;
  if (item.kind === "agent_approval") return <AgentApprovalCard item={item} />;
  if (item.kind === "unit_kit") return <KitApprovalCard item={item} />;
  if (item.kind === "request_answered") return <AnsweredCard item={item} selected={selected} onOpen={onOpen} />;
  if (item.kind === "code_review") return null; // drawn from its own query, with the review
  return <RequestCard item={item} selected={selected} onOpen={onOpen} />;
}

function Frame({ selected, children }: { selected?: boolean; children: React.ReactNode }) {
  return <Card className={cn("space-y-2.5", selected && "border-2 border-p-text")}>{children}</Card>;
}

function Top({ status, meta }: { status: "review" | "needs" | "blocked"; meta?: string | null }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Status tone={status} />
      {meta && <span className="text-meta text-p-text-muted">{meta}</span>}
    </div>
  );
}

/** Kostya's agent: the card opens to the full result and «как проверить» first. */
function ResultCard({ item, selected, onOpen, sentBack = false }: { item: WaitingItem; selected: boolean; onOpen: (id: string) => void; sentBack?: boolean }) {
  const [checking, setChecking] = useState(false);
  const task = useQuery({ queryKey: ["task", item.item.id], queryFn: () => Tasks.get(item.item.id), enabled: checking });
  return (
    <Frame selected={selected}>
      {sentBack
        ? <p className="text-meta text-p-text-muted">{[item.item.project, "ждёт новый результат — или подтверди этот как есть"].filter(Boolean).join(" · ")}</p>
        : <Top status="review" meta={item.item.project} />}
      <button type="button" className="text-left text-cell-title font-semibold text-p-text" onClick={() => onOpen(item.item.id)}>
        {item.item.title}
      </button>
      <Who item={item} />
      {!checking && item.text && <p className="line-clamp-3 text-body text-p-text">{item.text}</p>}
      {checking ? (
        task.data ? <TaskResultReview taskId={item.item.id} version={task.data.version} canReview />
          : <SkeletonList count={1} />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Button variant="strong" onClick={() => setChecking(true)}>{sentBack ? "Открыть результат" : "Проверить"}</Button>
          <Button variant="secondary" onClick={() => onOpen(item.item.id)}>Открыть задачу</Button>
        </div>
      )}
    </Frame>
  );
}

/**
 * 06.10: Kostya could not find where to approve — the card sat last and led to the task,
 * where the review was one more click away. Now it comes first and opens in place.
 */
function CodeReviewCard({ review, me, selected, onOpen }: {
  review: CodeReview; me?: string; selected: boolean; onOpen: (id: string, block?: string) => void;
}) {
  const [checking, setChecking] = useState(false);
  return (
    <Frame selected={selected}>
      <Top status="needs" meta={["Согласовать код", review.project_title].filter(Boolean).join(" · ")} />
      <button type="button" className="text-left text-cell-title font-semibold text-p-text" onClick={() => onOpen(review.task_id, "code")}>
        {review.task_title ?? "Проверка кода"}
      </button>
      {review.stale_reason && <p className="text-body text-p-blocked">{review.stale_reason}</p>}
      {checking ? (
        <CodeReviewPanel taskId={review.task_id} reviewId={review.id} me={me} canWrite={false} />
      ) : (
        <>
          <p className="line-clamp-3 text-body text-p-text">{review.summary}</p>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="strong" onClick={() => setChecking(true)}>Проверить</Button>
            <Button variant="secondary" onClick={() => onOpen(review.task_id, "code")}>Открыть задачу</Button>
          </div>
        </>
      )}
    </Frame>
  );
}

/**
 * Spec 052, step 6: a review of my client's order's code. The same decision as a task's code
 * review, right in the card; «Открыть заказ» leads to the order and its «Код».
 */
function OrderCodeReviewCard({ review, me }: { review: OrderCodeReview; me?: string }) {
  const [checking, setChecking] = useState(false);
  return (
    <Frame>
      <Top status="needs" meta={["Согласовать код заказа", review.order_label].filter(Boolean).join(" · ")} />
      <Link to={`/orders/${review.order_id}`} className="text-left text-cell-title font-semibold text-p-text">
        {review.order_title ?? "Код заказа"}
      </Link>
      {review.stale_reason && <p className="text-body text-p-blocked">{review.stale_reason}</p>}
      {checking && review.order_id ? (
        <OrderCodeReviewPanel orderId={review.order_id} item={review} me={me} />
      ) : (
        <>
          <p className="line-clamp-3 text-body text-p-text">{review.summary}</p>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="strong" onClick={() => setChecking(true)}>Проверить</Button>
            <Link to={`/orders/${review.order_id}`} className="inline-flex h-12 items-center justify-center rounded-xl border border-p-border bg-p-surface-2 px-6 text-[16px] font-semibold text-p-text press-surface">
              Открыть заказ
            </Link>
          </div>
        </>
      )}
    </Frame>
  );
}

/**
 * Spec 044 (Kostya, 19.10: «а почему выпуск не приходит в решить а нужно лезить в задачу?»):
 * merged code the server does not run yet. «Выпустить» right here — the same request as the
 * task's «Код» tab — and how the release goes; the card goes once the version is installed.
 */
export function ReleaseCard({ item, selected, onOpen }: {
  item: WaitingItem; selected: boolean; onOpen: (id: string, block?: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const state = item.release;
  const taskId = item.item.id;
  const [started, setStarted] = useState<string | null>(null);
  const watching = started ?? (state?.status === "queued" || state?.status === "running" ? state.release_id : null);
  const releases = useQuery({
    queryKey: ["code", taskId, "releases"],
    queryFn: async () => {
      const result = await Code.releases(taskId);
      const mine = result.releases.find((r) => r.id === watching);
      // Done or stopped: «Решить» is read again — an installed version leaves it.
      if (mine && mine.status !== "queued" && mine.status !== "running") void qc.invalidateQueries({ queryKey: ["overview"] });
      return result;
    },
    enabled: Boolean(watching),
    refetchInterval: 5_000,
  });
  const release = useMutation({
    mutationFn: () => Code.release(taskId, item.request_id ?? ""),
    onSuccess: (result) => {
      setStarted(result.id);
      toast.success("Выпуск начат: ход — здесь, в карточке");
      void qc.invalidateQueries({ queryKey: ["code", taskId] });
      void qc.invalidateQueries({ queryKey: ["overview"] });
    },
    onError: (e: Error) => {
      toast.error(e.message);
      void qc.invalidateQueries({ queryKey: ["overview"] });
    },
  });
  if (!state) return null;
  const live = releases.data?.releases.find((r) => r.id === watching);
  const status = live?.status ?? state.status;
  const phase = live?.phase ?? state.phase ?? status ?? "";
  const message = live?.message ?? state.message;
  // Running, done (until «Решить» is read again) or to be reconciled: no second release.
  const held = status === "queued" || status === "running" || status === "succeeded" || status === "unknown";
  const head = state.commit.slice(0, 8);
  return (
    <Frame selected={selected}>
      <Top status="needs" meta={["Выпустить", item.item.project].filter(Boolean).join(" · ")} />
      <button type="button" className="text-left text-cell-title font-semibold text-p-text" onClick={() => onOpen(taskId, "code")}>
        {item.item.title}
      </button>
      {item.by && <p className="text-meta text-p-text-muted">Согласовал {whoDid(item.by.name, item.via_agent)} · слито {ago(item.since)}</p>}
      <p className="line-clamp-3 text-body text-p-text">
        Слито {head}{state.summary ? ` — ${state.summary}` : ""}. На сервере {state.installed ? state.installed.slice(0, 8) : "более ранняя версия"}.
      </p>
      {state.closed && <p className="text-body text-p-blocked">Задача закрыта — переоткрой её, чтобы выпустить.</p>}
      {state.busy && !state.closed && <p className="text-body text-p-text-muted">Сейчас идёт другой выпуск — этот можно начать после него.</p>}
      {status && !state.busy && (
        <p role="status" className="rounded-xl bg-p-surface-2 p-3 text-body">
          {phases[phase] ?? phase}
          {status === "failed" && message ? `: ${message}` : ""}
          {status === "unknown" ? ". Исполнитель сверит сервер; новый выпуск пока заблокирован." : ""}
        </p>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Button variant="strong" disabled={state.closed || state.busy || held || release.isPending || !item.request_id} onClick={() => release.mutate()}>
          Выпустить {head}
        </Button>
        <Button variant="secondary" onClick={() => onOpen(taskId, "code")}>Открыть задачу</Button>
      </div>
    </Frame>
  );
}

/** Actions by role: the partner decides; the author discusses or withdraws. */
function IdeaCard({ item, me }: { item: WaitingItem; me?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  // One reason field: the partner rejects, the author withdraws — never both.
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const author = item.by?.id && item.by.id === me;
  const done = () => { for (const key of [["overview"], ["ideas"], ["tasks"]]) void qc.invalidateQueries({ queryKey: key }); };
  const approve = useMutation({
    mutationFn: async () => {
      const idea = await Ideas.peek(item.item.id);
      return Ideas.approve(item.item.id, { version: idea.version });
    },
    onSuccess: () => { toast.success("Идея стала задачей"); done(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const reject = useMutation({
    mutationFn: () => Ideas.reject(item.item.id, reason.trim()),
    onSuccess: () => { toast.success("Идея отклонена"); done(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const withdraw = useMutation({
    mutationFn: async () => {
      const idea = await Ideas.peek(item.item.id);
      return Ideas.withdraw(item.item.id, reason.trim(), idea.version);
    },
    onSuccess: () => { toast.success("Идея отозвана"); done(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const link = linkFor(item.item);
  return (
    <Frame>
      <Top status="needs" meta={item.by ? `идея ${genitive(item.by.name)}` : "идея"} />
      <Link to={link} className="text-cell-title font-semibold text-p-text">{item.item.title}</Link>
      {item.text && <p className="line-clamp-3 text-body text-p-text">{item.text}</p>}
      {rejecting ? (
        <div className="space-y-2">
          <TextArea label={author ? "Почему отзываешь — партнёр увидит" : "Почему отклоняем — автор увидит"} value={reason} rows={3} onChange={(e) => setReason(e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            {author
              ? <Button variant="strong" disabled={!reason.trim()} loading={withdraw.isPending} onClick={() => withdraw.mutate()}>Отозвать</Button>
              : <Button variant="strong" disabled={!reason.trim()} loading={reject.isPending} onClick={() => reject.mutate()}>Отклонить</Button>}
            <Button variant="secondary" onClick={() => setRejecting(false)}>Отмена</Button>
          </div>
        </div>
      ) : author ? (
        <div className="grid grid-cols-2 gap-2">
          <Link to={link} className="flex h-12 items-center justify-center rounded-xl bg-p-text text-[16px] font-semibold text-p-bg press-fill">Обсудить</Link>
          <Button variant="secondary" onClick={() => setRejecting(true)}>Отозвать</Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button variant="strong" className="min-w-0 flex-1 px-3" loading={approve.isPending} onClick={() => approve.mutate()}>В задачу</Button>
          <Link to={link} className="flex h-12 min-w-0 flex-1 items-center justify-center rounded-xl border border-p-border bg-p-surface-2 px-3 text-[16px] font-semibold text-p-text press-surface">Обсудить</Link>
          <Button variant="ghost" className="px-3" onClick={() => setRejecting(true)}>Отклонить</Button>
        </div>
      )}
    </Frame>
  );
}

/**
 * Spec 033: what a request is about — its task opens beside the list, anything else
 * (an idea, the shared chat) where it was asked.
 */
function RequestTitle({ item, onOpen }: { item: WaitingItem; onOpen: (id: string) => void }) {
  if (item.item.kind === "task") {
    return (
      <button type="button" className="text-left text-cell-title font-semibold text-p-text" onClick={() => onOpen(item.item.id)}>
        {item.item.title}
      </button>
    );
  }
  return <Link to={item.link ?? linkFor(item.item)} className="text-left text-cell-title font-semibold text-p-text">{item.item.title}</Link>;
}

/** One move on a request with its own retry key; a new key after every answer from the server. */
function useRequestMove(item: WaitingItem, done: (body: PersonRequestMove) => string) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = useRef(crypto.randomUUID());
  return useMutation({
    mutationFn: (body: PersonRequestMove) => PersonRequests.move(item.request_id!, body, key.current),
    onSuccess: (_, body) => {
      key.current = crypto.randomUUID();
      toast.success(done(body));
      void qc.invalidateQueries({ queryKey: ["chat"] });
      void qc.invalidateQueries({ queryKey: ["overview"] });
      // Spec 034: an accepted handover changes the task's assignee.
      if (item.request_kind === "handover") void qc.invalidateQueries({ queryKey: ["task"] });
    },
    onError: (e: Error) => {
      key.current = crypto.randomUUID();
      toast.error(e.message);
      void qc.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}

/**
 * Spec 033: a request addressed to me — answered right here: one of its answers, or my own
 * words and «Отправить». The answer is bound to the request; reading it or other words
 * nearby never close it. A personal one waits for me only; otherwise my agent may answer too.
 * Spec 034: a handover is answered with its buttons only — «Принимаю задачу» makes it mine.
 */
function RequestCard({ item, selected, onOpen }: { item: WaitingItem; selected: boolean; onOpen: (id: string) => void }) {
  const [text, setText] = useState("");
  const handover = item.request_kind === "handover";
  const send = useRequestMove(item, (body) => {
    setText("");
    if (handover && "option" in body) return body.option === 0 ? "Задача теперь твоя" : "Передача отклонена";
    return "Ответ отправлен";
  });
  if (!item.request_id) return null;
  const meta = [
    handover ? "Передача задачи" : null,
    item.item.project,
    item.mode === "personal" && !handover ? "нужен твой личный ответ" : null,
  ].filter(Boolean).join(" · ");
  return (
    <Frame selected={selected}>
      <Top status={item.blocking ? "blocked" : "needs"} meta={meta || null} />
      <RequestTitle item={item} onOpen={onOpen} />
      <Who item={item} />
      {item.text && <p className="break-words text-body text-p-text">{item.text}</p>}
      {item.blocking && (
        <p className="text-body text-p-text"><span className="text-p-text-muted">Стоит: </span>{item.blocking_note ?? "работа ждёт ответа"}</p>
      )}
      {(item.options ?? []).length > 0 && (
        <div className="flex flex-col gap-2">
          {(item.options ?? []).map((option, i) => (
            <Button key={option} block variant={i === 0 ? "strong" : "secondary"} disabled={send.isPending}
              onClick={() => send.mutate({ action: "answer", option: i })}>
              {option}
            </Button>
          ))}
        </div>
      )}
      {handover && <p className="text-meta text-p-text-muted">Сводка передачи — в обсуждении задачи. Примешь — станешь исполнителем.</p>}
      {!handover && <div className="flex items-end gap-2">
        <div className="grow-wrap min-w-0 flex-1" data-value={text}>
          <textarea value={text} rows={1} onChange={(e) => setText(e.target.value)} placeholder="Ответить…" aria-label="Ответ"
            className="rounded-[22px] border border-p-border-strong bg-p-surface text-p-text placeholder:text-p-text-muted focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-p-focus" />
        </div>
        <Button variant="strong" size="compact" disabled={!text.trim()} loading={send.isPending}
          onClick={() => send.mutate({ action: "answer", text: text.trim() })}>
          Отправить
        </Button>
      </div>}
    </Frame>
  );
}

/**
 * Spec 033: the answer to my own request came — «Принять» takes it and closes the request;
 * «Уточнить» sends it back with my words. An agent's answer is said to be one.
 */
function AnsweredCard({ item, selected, onOpen }: { item: WaitingItem; selected: boolean; onOpen: (id: string) => void }) {
  const [clarifying, setClarifying] = useState(false);
  const [text, setText] = useState("");
  const move = useRequestMove(item, (body) => {
    setText("");
    setClarifying(false);
    return body.action === "accept" ? "Ответ принят" : "Уточнение отправлено";
  });
  if (!item.request_id) return null;
  return (
    <Frame selected={selected}>
      <Top status="review" meta={[item.request_kind === "handover" ? "Ответ на передачу задачи" : "Ответ на твой запрос", item.item.project].filter(Boolean).join(" · ")} />
      <RequestTitle item={item} onOpen={onOpen} />
      <Who item={item} />
      {item.text && <p className="break-words text-body text-p-text-muted">Запрос: {item.text}</p>}
      {item.answer && <p className="break-words text-body text-p-text"><span className="text-p-text-muted">Ответ: </span>{item.answer}</p>}
      {clarifying ? (
        <div className="space-y-2">
          <TextArea label="Что уточнить — уйдёт адресату" value={text} rows={3} onChange={(e) => setText(e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <Button variant="strong" disabled={!text.trim()} loading={move.isPending} onClick={() => move.mutate({ action: "clarify", text: text.trim() })}>Отправить</Button>
            <Button variant="secondary" onClick={() => setClarifying(false)}>Отмена</Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Button variant="strong" loading={move.isPending} onClick={() => move.mutate({ action: "accept" })}>Принять</Button>
          {item.request_kind !== "handover" && <Button variant="secondary" disabled={move.isPending} onClick={() => setClarifying(true)}>Уточнить</Button>}
        </div>
      )}
    </Frame>
  );
}

/**
 * Spec 027, D9: what a run of an agent waits for from me. A question: one of its answers, or
 * my own words. A right: for this run only, or no. The answer goes to the run, which reads it
 * in list_run_requests. Spec 031 A1 §5.3: Claude Code's permission request — the tool and its
 * input as it asks, allowed once or refused; the folder's hook answers the terminal with it.
 */
function AgentRequestCard({ item }: { item: WaitingItem }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState("");
  const kind: AgentRequestKind = item.request_kind && item.request_kind !== "handover" ? item.request_kind : "question";
  const decide = useMutation({
    mutationFn: (body: AgentRequestDecision) => AgentRequests.decide(item.request_id!, body),
    onSuccess: (_, body) => {
      setText("");
      toast.success(decidedText(kind, body.decision, (body as { always?: boolean }).always));
      void qc.invalidateQueries({ queryKey: ["overview"] });
      void qc.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (e: Error) => {
      toast.error(e.message);
      void qc.invalidateQueries({ queryKey: ["overview"] });
    },
  });
  if (!item.request_id) return null;
  return (
    <Frame>
      <Top status="needs" meta={`${REQUEST_KIND[kind]} · ${waitsLine(kind, item.since)}`} />
      <Link to={linkFor(item.item)} className="text-left text-cell-title font-semibold text-p-text">{item.item.title}</Link>
      <Who item={item} />
      {/* Spec 040: what the staff member did before it asked, and what it does now. */}
      <Link to={`/agents/${item.item.id}?block=feed`} className="inline-flex min-h-11 items-center self-start text-meta font-semibold text-p-link">
        Лента сотрудника
      </Link>
      {item.text && <p className="break-words text-body text-p-text">{item.text}</p>}
      {kind === "card_change" ? (
        <CardChangeBody item={item} />
      ) : kind === "permission" && item.permission ? (
        <PermissionBody item={item} pending={decide.isPending}
          onDecide={(decision, always) => decide.mutate({ decision, ...(always ? { always } : {}) } as AgentRequestDecision)} />
      ) : kind === "question" ? (
        <>
          {(item.options ?? []).length > 0 && (
            <div className="flex flex-col gap-2">
              {(item.options ?? []).map((option, i) => (
                <Button key={option} block variant={i === 0 ? "strong" : "secondary"} disabled={decide.isPending}
                  onClick={() => decide.mutate({ decision: "answer", option: i })}>
                  {option}
                </Button>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <div className="grow-wrap min-w-0 flex-1" data-value={text}>
              <textarea value={text} rows={1} onChange={(e) => setText(e.target.value)} placeholder="Свой ответ…" aria-label="Свой ответ"
                className="rounded-[22px] border border-p-border-strong bg-p-surface text-p-text placeholder:text-p-text-muted focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-p-focus" />
            </div>
            <Button variant="secondary" size="compact" disabled={!text.trim()} loading={decide.isPending}
              onClick={() => decide.mutate({ decision: "answer", answer: text.trim() })}>
              Ответить
            </Button>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap gap-2">
          {item.can_grant === false ? (
            // A service with no limit on the card: nothing to extend, the text says what to do.
            <Button variant="strong" size="compact" loading={decide.isPending} onClick={() => decide.mutate({ decision: "refuse" })}>
              {decisionLabels(kind).refuse}
            </Button>
          ) : (
            <>
              <Button variant="strong" size="compact" loading={decide.isPending} onClick={() => decide.mutate({ decision: "grant" })}>
                {decisionLabels(kind).grant}
              </Button>
              <Button variant="secondary" size="compact" disabled={decide.isPending} onClick={() => decide.mutate({ decision: "refuse" })}>
                {decisionLabels(kind).refuse}
              </Button>
            </>
          )}
        </div>
      )}
    </Frame>
  );
}

/** What Claude Code asks to run, whole as far as kept, and «Разрешить один раз» / «Отказать».
 * Spec 041: «Разрешить и не спрашивать: …» too, when the server offers a rule of this question. */
function PermissionBody({ item, pending, onDecide }: {
  item: WaitingItem;
  pending: boolean;
  onDecide: (decision: "grant" | "refuse", always?: boolean) => void;
}) {
  const asked = item.permission!;
  const note = permissionNote(asked);
  const labels = decisionLabels("permission");
  const offer = ruleOffer(asked);
  return (
    <>
      <pre aria-label={`Что просит ${asked.tool}`}
        className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-card border border-p-border bg-p-surface-2 px-3 py-2 font-mono text-meta text-p-text">
        {asked.input}
      </pre>
      {note.cut && <p className="text-meta text-p-blocked">{note.cut}</p>}
      <p className="text-meta text-p-text-muted">{note.wait}</p>
      {note.open && (
        <div className="flex flex-wrap gap-2">
          <Button variant="strong" size="compact" loading={pending} onClick={() => onDecide("grant")}>{labels.grant}</Button>
          {offer && <Button variant="secondary" size="compact" disabled={pending} onClick={() => onDecide("grant", true)}>{ruleLabel(offer)}</Button>}
          <Button variant="secondary" size="compact" disabled={pending} onClick={() => onDecide("refuse")}>{labels.refuse}</Button>
        </div>
      )}
    </>
  );
}

/**
 * Spec 043: a staff member's new version — or its helpers' — waits for my «Одобрить». «Что
 * изменилось» opens the approval sheet of the agent's own card: what changed since my version,
 * and «Одобрить» for the current one with its helpers. Helpers alone: their changes are in their
 * own cards, and one «Одобрить помощников» is here. The item goes once the server sees it done.
 */
export function AgentApprovalCard({ item }: { item: WaitingItem }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [approving, setApproving] = useState(false);
  const agent = useQuery({ queryKey: ["agents", "one", item.item.id], queryFn: () => Agents.get(item.item.id) });
  const need = agent.data ? approvalNeed(agent.data) : undefined;
  const helpers = useMutation({
    mutationFn: () => Agents.approve(item.item.id, agent.data!.version),
    onSuccess: () => {
      toast.success("Помощники одобрены для твоего компьютера");
      void qc.invalidateQueries({ queryKey: ["agents"] });
      void qc.invalidateQueries({ queryKey: ["overview"] });
    },
    onError: (e: Error) => {
      toast.error(e.message);
      void qc.invalidateQueries({ queryKey: ["agents"] });
      void qc.invalidateQueries({ queryKey: ["overview"] });
    },
  });
  return (
    <Frame>
      <Top status="needs" meta="Одобрить версию" />
      <Link to={linkFor(item.item)} className="text-left text-cell-title font-semibold text-p-text">{item.item.title}</Link>
      <Who item={item} />
      {item.text && <p className="break-words text-body text-p-text">{item.text}</p>}
      <div className="grid grid-cols-2 gap-2">
        {need === "helpers" ? (
          <Button variant="strong" loading={helpers.isPending} onClick={() => helpers.mutate()}>Одобрить помощников</Button>
        ) : (
          <Button variant="strong" disabled={!agent.data} loading={agent.isLoading} onClick={() => setApproving(true)}>Что изменилось</Button>
        )}
        <Link to={linkFor(item.item)} className="flex h-12 items-center justify-center rounded-xl border border-p-border bg-p-surface-2 px-3 text-[16px] font-semibold text-p-text press-surface">
          Открыть карточку
        </Link>
      </div>
      {approving && agent.data && (
        <ApproveSheet agent={agent.data} onClose={() => { setApproving(false); void qc.invalidateQueries({ queryKey: ["overview"] }); }} />
      )}
    </Frame>
  );
}

/**
 * Spec 055: a unit's kit version waits for a «да» — one of any of the top unit's people. The
 * card shows the difference against the version in force in words («+» came, «−» went) and
 * approves that very version right here; the unit's page («Набор») has the whole kit. An older
 * server sends no `kit`: the card keeps its text and leads to the unit.
 */
export function KitApprovalCard({ item }: { item: WaitingItem }) {
  const qc = useQueryClient();
  const toast = useToast();
  const kit = item.kit ?? null;
  const approve = useMutation({
    mutationFn: () => UnitKits.approve(item.item.id, kit!.version),
    onSuccess: () => {
      toast.success("Набор отдела одобрен: сотрудники получат его со следующего запуска");
      void qc.invalidateQueries({ queryKey: ["overview"] });
      void qc.invalidateQueries({ queryKey: ["units"] });
      void qc.invalidateQueries({ queryKey: ["departments"] });
    },
    onError: (e: Error) => {
      toast.error(e.message);
      void qc.invalidateQueries({ queryKey: ["overview"] });
    },
  });
  const title = kit ? `Набор отдела «${item.item.title}», версия ${kit.version}` : `Набор отдела «${item.item.title}»`;
  return (
    <Frame>
      <Top status="needs" meta="Одобрить набор отдела" />
      <Link to={linkFor(item.item)} className="text-left text-cell-title font-semibold text-p-text">{title}</Link>
      <Who item={item} />
      {kit && (
        <p className="text-meta text-p-text-muted">
          {kit.in_force ? `Сейчас действует версия ${kit.in_force}` : "Пока набора ещё нет: это первая версия"}
          {" · "}
          Одобряет любой один из людей отдела; агентов заново одобрять не нужно.
        </p>
      )}
      {kit && kit.lines.length > 0 ? (
        <ul className="space-y-0.5 text-body text-p-text">
          {kit.lines.map((line) => (
            <li key={`${line.sign}${line.text}`} className={line.sign === "-" ? "text-p-text-muted" : undefined}>
              {line.sign === "+" ? "+" : "−"} {line.text}
            </li>
          ))}
        </ul>
      ) : (
        item.text && <p className="break-words text-body text-p-text">{item.text}</p>
      )}
      <div className="grid grid-cols-2 gap-2">
        {kit && (
          <Button variant="strong" loading={approve.isPending} onClick={() => approve.mutate()}>
            Одобрить версию {kit.version}
          </Button>
        )}
        <Link to={linkFor(item.item)} className="flex h-12 items-center justify-center rounded-xl border border-p-border bg-p-surface-2 px-3 text-[16px] font-semibold text-p-text press-surface">
          Открыть отдел
        </Link>
      </div>
    </Frame>
  );
}

/** Spec 043: the approval sheet a link from Telegram asks for, over «Решить». */
function ApproveFromLink({ agentId, onClose }: { agentId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const agent = useQuery({ queryKey: ["agents", "one", agentId], queryFn: () => Agents.get(agentId) });
  if (!agent.data) return null;
  return <ApproveSheet agent={agent.data} onClose={() => { onClose(); void qc.invalidateQueries({ queryKey: ["overview"] }); }} />;
}

/**
 * Spec 025, D4: one card for every case on review, however many a case factory
 * turned out; either person decides, so both see it. It opens the filtered list.
 */
export function CasesCard({ count }: { count: number }) {
  return (
    <Frame>
      <Top status="needs" meta="Пример решения" />
      <Link to="/knowledge/cases?f=review" className="text-cell-title font-semibold text-p-text">Кейсы на проверке: {count}</Link>
      <p className="text-body text-p-text-muted">Прочитай и прими — или верни с комментарием, что поправить.</p>
      <div>
        <Link to="/knowledge/cases?f=review" className="inline-flex h-12 items-center justify-center rounded-xl bg-p-text px-6 text-[16px] font-semibold text-p-bg press-fill">
          Открыть
        </Link>
      </div>
    </Frame>
  );
}

/**
 * Task d9165be7: my open task past its deadline, until it is closed or the deadline moves.
 * «Перенести срок» sets a new date right here — the end of that day in my time zone — with
 * the task's version read just before, so a newer edit is never overwritten (SPEC-2 §7).
 */
function OverdueCard({ item, selected, onOpen }: { item: WaitingItem; selected: boolean; onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [moving, setMoving] = useState(false);
  const [day, setDay] = useState("");
  const past = Boolean(day) && day < localDay();
  const move = useMutation({
    mutationFn: async () => {
      const task = await Tasks.get(item.item.id);
      return Tasks.update(item.item.id, { due_at: deadlineOf(day), version: task.version });
    },
    onSuccess: () => {
      toast.success("Срок перенесён");
      setMoving(false);
      setDay("");
      void qc.invalidateQueries({ queryKey: ["overview"] });
      void qc.invalidateQueries({ queryKey: ["task", item.item.id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Frame selected={selected}>
      <Top status="needs" meta={["Срок прошёл", item.item.project].filter(Boolean).join(" · ")} />
      <button type="button" className="text-left text-cell-title font-semibold text-p-text" onClick={() => onOpen(item.item.id)}>
        {item.item.title}
      </button>
      {item.text && <p className="text-body text-p-text">{item.text}</p>}
      {moving ? (
        <div className="space-y-2">
          <DateField label="Новый срок" value={day} onChange={setDay} placeholder="Выбери дату" error={past ? "Выбери сегодня или позже" : undefined} />
          <div className="grid grid-cols-2 gap-2">
            <Button variant="strong" disabled={!day || past} loading={move.isPending} onClick={() => move.mutate()}>Перенести</Button>
            <Button variant="secondary" onClick={() => setMoving(false)}>Отмена</Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Button variant="strong" onClick={() => setMoving(true)}>Перенести срок</Button>
          <Button variant="secondary" onClick={() => onOpen(item.item.id)}>Открыть задачу</Button>
        </div>
      )}
    </Frame>
  );
}

function BlockedCard({ task, selected, onOpen }: { task: ActiveTask; selected: boolean; onOpen: (id: string) => void }) {
  return (
    <Frame selected={selected}>
      <Top status="blocked" meta={task.item.project} />
      <button type="button" className="text-left text-cell-title font-semibold text-p-text" onClick={() => onOpen(task.item.id)}>
        {task.item.title}
      </button>
      {task.last_entry?.blocker && <p className="text-body text-p-text"><span className="text-p-text-muted">Препятствие: </span>{task.last_entry.blocker}</p>}
      <div><Button variant="secondary" size="compact" onClick={() => onOpen(task.item.id)}>Открыть задачу</Button></div>
    </Frame>
  );
}

/**
 * A row that opens its item: a task opens where the cards open theirs — beside the
 * list on a computer — instead of leaving «Решить» (PC audit 28.09, point 9).
 */
function Opens({ item, onOpen, className, children }: { item: WaitingItem["item"]; onOpen: (id: string) => void; className: string; children: React.ReactNode }) {
  if (item.kind === "task") {
    return <button type="button" onClick={() => onOpen(item.id)} className={cn("w-full text-left", className)}>{children}</button>;
  }
  return <Link to={linkFor(item)} className={className}>{children}</Link>;
}

/** One quiet block: who is working, what waits for the partner. */
function Happening({ data, onOpen }: { data: OverviewOut; onOpen: (id: string) => void }) {
  const agents = data.active
    .filter((a) => a.last_entry?.via_agent && a.last_entry.at && new Date(data.generated_at).getTime() - new Date(a.last_entry.at).getTime() < 6 * 3_600_000)
    .slice(0, 3);
  const others = waitingOnOthers(data);
  if (!agents.length && !others.ideas.length && !others.items.length) return null;
  return (
    <section className="mt-2 space-y-2">
      <Caption>Что происходит</Caption>
      <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
        {agents.map((a) => (
          <li key={a.item.id} className="border-b border-p-border px-4 py-3 last:border-0">
            <Opens item={a.item} onOpen={onOpen} className="block">
              <span className="block text-body font-semibold text-p-text">Агент {genitive(a.last_entry!.author.name)}: {a.item.title}</span>
              <span className="block truncate text-meta text-p-text-muted">{a.last_entry!.summary ?? a.last_entry!.current_stage} · {ago(a.last_entry!.at)}</span>
            </Opens>
          </li>
        ))}
        {others.ideas.map(({ name, count }) => (
          <li key={`ideas-${name}`} className="border-b border-p-border last:border-0">
            <Link to="/ideas?f=awaiting" className="block px-4 py-3 press-surface">
              <span className="block text-body font-semibold text-p-text">Твои идеи ждут решения {genitive(name)}</span>
              <span className="block text-meta text-p-text-muted">{count} {ideasWord(count)} · открыть список</span>
            </Link>
          </li>
        ))}
        {others.items.slice(0, 5).map((w) => (
          <li key={`${w.kind}-${w.request_id ?? w.item.id}`} className="border-b border-p-border last:border-0">
            <Opens item={w.item} onOpen={onOpen} className="block px-4 py-3 press-surface">
              <span className="block text-body font-semibold text-p-text">Ждёт {genitive(w.waiting_on.name)}: {w.item.title}</span>
              <span className="block truncate text-meta text-p-text-muted">{w.kind === "result_review" ? "проверить результат" : w.text ?? "ответить на запрос"}</span>
            </Opens>
          </li>
        ))}
        {others.items.length > 5 && (
          <li className="px-4 py-3 text-meta text-p-text-muted">и ещё {others.items.length - 5}</li>
        )}
      </ul>
    </section>
  );
}
